import { Stage } from "./types";
import { tokenStore } from "./services/tokenStore";

export const resolveStageFromHostname = (hostname: string): Stage => {
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "dev";
  }
  return hostname.startsWith("dev-") ? "dev" : "prod";
};

const stage = resolveStageFromHostname(window.location.hostname);

const apiBaseByStage: Record<Stage, string> = {
  dev: "https://8p60ot8y7a.execute-api.us-east-2.amazonaws.com/api/v1",
  prod: "https://iqdxbpogz1.execute-api.us-east-1.amazonaws.com/api/v1"
};

const rcApiBaseByStage: Record<Stage, string> = {
  dev: "https://stage-api.bellomyonline.com",
  prod: "https://api.bellomyonline.com"
};

/** Research Cloud API base URL selected independently from the Panel Insights API. */
export const resolveRcApiBaseUrl = (hostname: string): string =>
  rcApiBaseByStage[resolveStageFromHostname(hostname)];

export const appConfig = {
  stage,
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || apiBaseByStage[stage],
  briAuthBaseUrl: process.env.REACT_APP_BRI_AUTH_BASE_URL || "https://auth.bellomyonline.com",
  briAuthApiBaseUrl: process.env.REACT_APP_BRI_API_BASE_URL || "https://authapi.bellomyonline.com",
  rcApiBaseUrl: process.env.REACT_APP_RC_API_BASE_URL || resolveRcApiBaseUrl(window.location.hostname)
};

const AUDIENCE_PATTERN = /^[A-Z0-9][A-Z0-9._:-]{0,127}$/;

export class AudienceResolutionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AudienceResolutionError";
  }
}

const normalizeAudience = (value: string): string => {
  const audience = value.trim().toUpperCase();
  if (!AUDIENCE_PATTERN.test(audience)) {
    throw new AudienceResolutionError("The audience in this link is invalid.");
  }
  return audience;
};

/** Audience from claims verified for the logged-in user's current JWT. */
const getAudienceFromUserClaims = (): string | null => {
  const token = tokenStore.getToken();
  if (!token) {
    return null;
  }
  const claims = tokenStore.getCachedClaims(token);
  if (!claims) {
    return null;
  }
  const aud = claims.audienceName || claims.userAudience;
  return aud ? normalizeAudience(aud) : null;
};

/** Audience from a supported pre-login query-string parameter. */
export const resolveAudienceFromQuery = (searchValue: string): string | null => {
  const search = new URLSearchParams(searchValue);
  const queryAudience = search.get("audience") ?? search.get("adnc") ?? search.get("ADNC");
  return queryAudience === null ? null : normalizeAudience(queryAudience);
};

/** Legacy audience-bearing hostnames remain supported as the lowest-priority source. */
export const resolveAudienceFromHostname = (hostname: string): string | null => {
  const devMatch = hostname.match(/^dev-([a-z0-9-]+)-(?:insights|paneladmin)\.bellomyonline\.com$/i);
  if (devMatch?.[1]) {
    return normalizeAudience(devMatch[1]);
  }

  const prodMatch = hostname.match(/^([a-z0-9-]+)-(?:insights|paneladmin)\.bellomyonline\.com$/i);
  return prodMatch?.[1] ? normalizeAudience(prodMatch[1]) : null;
};

export const resolveAudienceForRequest = (
  claimsAudience: string | null,
  search: string,
  hostname: string
): string => {
  if (claimsAudience) {
    return normalizeAudience(claimsAudience);
  }

  const queryAudience = resolveAudienceFromQuery(search);
  if (queryAudience) {
    return queryAudience;
  }

  const hostnameAudience = resolveAudienceFromHostname(hostname);
  if (hostnameAudience) {
    return hostnameAudience;
  }

  throw new AudienceResolutionError(
    "This link does not identify an audience. Request a valid link from your research administrator."
  );
};

export const getAudience = (): string => {
  // A verified session wins over navigation input. Query-string selection is
  // next, with the legacy audience-bearing hostname retained only as a final
  // compatibility source. Merely having an unverified token is not sufficient.
  return resolveAudienceForRequest(
    getAudienceFromUserClaims(),
    window.location.search,
    window.location.hostname
  );
};

export const authEndpoints = {
  get loginUrl() {
    return appConfig.briAuthBaseUrl;
  },
  get verifyUrl() {
    return `${appConfig.briAuthApiBaseUrl}/verify?audience=${encodeURIComponent(getAudience())}`;
  },
  get refreshUrl() {
    return `${appConfig.briAuthApiBaseUrl}/refresh?audience=${encodeURIComponent(getAudience())}`;
  },
};
